import React from "react";

import { Grid, Skeleton, Stack, useTheme, alpha } from "@mui/material";

/**
 * Loading skeleton for the Organization Details page.
 *
 * Mirrors the Overview tab's 2-column card grid (per States skill:
 * "Loading states match layout shape") so the transition to real content
 * produces minimal layout shift (CLS < 0.05).
 *
 * Known limitation: the skeleton renders during the initial `loading` state
 * before the active tab is known. Deep-linking to ?tab=settings will briefly
 * show the overview skeleton, then snap to settings. This is acceptable —
 * the skeleton reduces CLS for the common case (overview). A tab-aware
 * skeleton would require knowing the tab before data loads, which isn't
 * possible during the initial fetch.
 */
const OrganizationDetailsSkeleton = () => {
  const theme = useTheme();

  const cardSx = {
    borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
    p: 2,
    bgcolor: "background.paper",
  };

  const labelSkeleton = <Skeleton variant="text" width={90} height={14} />;

  return (
    <Stack spacing={3}>
      {/* Header card skeleton */}
      <Stack sx={cardSx} spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="text" width={180} height={28} />
          <Skeleton variant="rounded" width={100} height={22} />
        </Stack>
        <Skeleton variant="text" width="70%" height={16} />
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4}>
            <Stack alignItems="center" spacing={0.5} sx={{ p: 2 }}>
              <Skeleton variant="circular" width={18} height={18} />
              <Skeleton variant="text" width={32} height={28} />
              <Skeleton variant="text" width={60} height={12} />
            </Stack>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Stack alignItems="center" spacing={0.5} sx={{ p: 2 }}>
              <Skeleton variant="circular" width={18} height={18} />
              <Skeleton variant="text" width={32} height={28} />
              <Skeleton variant="text" width={60} height={12} />
            </Stack>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Stack alignItems="center" spacing={0.5} sx={{ p: 2 }}>
              <Skeleton variant="circular" width={18} height={18} />
              <Skeleton variant="text" width={32} height={28} />
              <Skeleton variant="text" width={60} height={12} />
            </Stack>
          </Grid>
        </Grid>
      </Stack>

      {/* Tab bar skeleton — tinted strip, no border */}
      <Stack
        direction="row"
        spacing={2}
        sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          borderRadius: 1,
          p: 1,
        }}
      >
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="text"
            width={60 + (i % 3) * 20}
            height={24}
          />
        ))}
      </Stack>

      {/* Overview content — 2-column card grid */}
      <Grid container spacing={2}>
        {/* Card A — MTD Spend (left top) */}
        <Grid item xs={12} md={5}>
          <Stack sx={cardSx} spacing={1.5}>
            {labelSkeleton}
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Skeleton variant="rounded" width={36} height={36} />
              <Stack spacing={0.5}>
                <Skeleton variant="text" width={80} height={20} />
                <Skeleton variant="text" width={60} height={12} />
              </Stack>
            </Stack>
          </Stack>
        </Grid>

        {/* Card B — Stacks Health (right top) */}
        <Grid item xs={12} md={7}>
          <Stack sx={cardSx} spacing={1.5}>
            {labelSkeleton}
            <Stack direction="row" spacing={1}>
              <Skeleton variant="rounded" width={80} height={22} />
              <Skeleton variant="rounded" width={80} height={22} />
              <Skeleton variant="rounded" width={80} height={22} />
            </Stack>
            <Skeleton variant="text" width="100%" height={14} />
            <Skeleton variant="text" width="70%" height={14} />
          </Stack>
        </Grid>

        {/* Card C — Quick Actions (left below) */}
        <Grid item xs={12} md={5}>
          <Stack sx={cardSx} spacing={1.5}>
            {labelSkeleton}
            <Skeleton variant="rounded" width="100%" height={36} />
            <Skeleton variant="rounded" width="100%" height={36} />
            <Skeleton variant="rounded" width="100%" height={36} />
          </Stack>
        </Grid>

        {/* Card D — Recent Activity (right below) */}
        <Grid item xs={12} md={7}>
          <Stack sx={cardSx} spacing={1.5}>
            {labelSkeleton}
            {Array.from({ length: 4 }).map((_, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center">
                <Skeleton variant="circular" width={8} height={8} />
                <Skeleton variant="text" width="55%" height={14} />
                <Skeleton
                  variant="text"
                  width={40}
                  height={14}
                  sx={{ ml: "auto" }}
                />
              </Stack>
            ))}
          </Stack>
        </Grid>

        {/* Card E — Stacks table (full width) */}
        <Grid item xs={12}>
          <Stack sx={cardSx} spacing={1.5}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              {labelSkeleton}
              <Skeleton variant="text" width={40} height={14} />
            </Stack>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" width="100%" height={28} />
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default OrganizationDetailsSkeleton;
